import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useContactQuotes, useContactSquareStatus, useUpdateContact, type Contact } from "@/hooks/useContacts";
import { OpenInGhlButton } from "@/components/OpenInGhlButton";
import { PushToSquareButton } from "@/components/contacts/PushToSquareButton";
import { PIPELINE_STAGE_LABELS, PIPELINE_STAGE_COLORS, type PipelineStage, SERVICE_TYPE_LABELS, type ServiceType } from "@/types/quotes";
import { Mail, Phone, CheckCircle2, XCircle, Pencil, Save, X, Calendar, DollarSign, FileText } from "lucide-react";
import { format } from "date-fns";


interface Props {
  contact: Contact | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onQuoteClick?: (quoteId: string) => void;
}

export function ContactDetailSheet({ contact, open, onOpenChange, onQuoteClick }: Props) {
  const { data: quotes, isLoading: quotesLoading } = useContactQuotes(contact?.id ?? null);
  const { data: squareStatus } = useContactSquareStatus(contact?.id ?? null);
  const updateContact = useUpdateContact();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });

  const startEdit = () => {
    if (!contact) return;
    setForm({
      first_name: contact.first_name,
      last_name: contact.last_name || "",
      email: contact.email ?? "",
      phone: contact.phone || "",
    });
    setEditing(true);
  };

  const saveEdit = () => {
    if (!contact) return;
    updateContact.mutate(
      { id: contact.id, first_name: form.first_name, last_name: form.last_name || null, email: form.email || null, phone: form.phone || null },
      { onSuccess: () => setEditing(false) }
    );
  };

  if (!contact) return null;

  const totalValue = quotes?.reduce((s, q) => s + Number(q.total), 0) ?? 0;
  const totalPaid = quotes?.reduce((s, q) => s + Number(q.amount_paid), 0) ?? 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            <span>{contact.first_name} {contact.last_name || ""}</span>
            {!editing && (
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={startEdit}>
                <Pencil className="h-4 w-4" />
              </Button>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-6 mt-4">
          {/* Contact Info */}
          {editing ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>First Name</Label>
                  <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={saveEdit} disabled={updateContact.isPending}>
                  <Save className="h-3.5 w-3.5 mr-1" />
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                  <X className="h-3.5 w-3.5 mr-1" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {contact.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <a href={`mailto:${contact.email}`} className="text-primary hover:underline">{contact.email}</a>
                </div>
              )}
              {contact.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <a href={`tel:${contact.phone}`} className="hover:underline">{contact.phone}</a>
                </div>
              )}
            </div>
          )}

          {/* GHL Sync Status */}
          <div className="flex items-center gap-2">
            {contact.ghl_contact_id ? (
              <Badge variant="secondary" className="gap-1 text-xs">
                <CheckCircle2 className="h-3 w-3 text-primary" />
                Synced with GHL
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1 text-xs">
                <XCircle className="h-3 w-3 text-muted-foreground" />
                Local only
              </Badge>
            )}
            {contact.last_synced_at && (
              <span className="text-xs text-muted-foreground">
                Last synced {format(new Date(contact.last_synced_at), "MMM d, yyyy h:mm a")}
              </span>
            )}
            <OpenInGhlButton
              ghlContactId={contact.ghl_contact_id}
              ghlLocationId={contact.ghl_location_id}
              className="ml-auto"
            />
          </div>

          {/* Square Sync Status (#803) — status read via useContactSquareStatus,
              NOT off the passed contact prop (which may be a stats-RPC snapshot
              without the square columns). */}
          <div className="flex items-center gap-2">
            {squareStatus?.square_customer_id ? (
              <Badge variant="secondary" className="gap-1 text-xs">
                <CheckCircle2 className="h-3 w-3 text-primary" />
                Synced with Square
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1 text-xs">
                <XCircle className="h-3 w-3 text-muted-foreground" />
                Not in Square
              </Badge>
            )}
            {squareStatus?.square_synced_at && (
              <span className="text-xs text-muted-foreground">
                Last pushed {format(new Date(squareStatus.square_synced_at), "MMM d, yyyy h:mm a")}
              </span>
            )}
            <PushToSquareButton contactId={contact.id} className="ml-auto" />
          </div>

          <Separator />

          {/* Activity Summary */}
          <div>
            <h4 className="text-sm font-semibold mb-3">Activity Summary</h4>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border bg-card p-3 text-center">
                <div className="text-2xl font-bold">{quotes?.length ?? 0}</div>
                <div className="text-xs text-muted-foreground">Quotes</div>
              </div>
              <div className="rounded-lg border bg-card p-3 text-center">
                <div className="text-2xl font-bold">${totalValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
                <div className="text-xs text-muted-foreground">Total Value</div>
              </div>
              <div className="rounded-lg border bg-card p-3 text-center">
                <div className="text-2xl font-bold">${totalPaid.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
                <div className="text-xs text-muted-foreground">Paid</div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Linked Quotes */}
          <div>
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Linked Quotes
            </h4>
            {quotesLoading ? (
              <div className="space-y-2">
                {[1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            ) : !quotes?.length ? (
              <p className="text-sm text-muted-foreground py-4 text-center">No quotes linked to this contact.</p>
            ) : (
              <div className="space-y-2">
                {quotes.map((q) => (
                  <button
                    key={q.id}
                    onClick={() => {
                      if (onQuoteClick) {
                        onOpenChange(false);
                        onQuoteClick(q.id);
                      }
                    }}
                    className="w-full text-left rounded-lg border bg-card p-3 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{q.quote_number}</span>
                      <Badge variant="outline" className={`text-xs ${PIPELINE_STAGE_COLORS[q.pipeline_stage as PipelineStage] || ""}`}>
                        {PIPELINE_STAGE_LABELS[q.pipeline_stage as PipelineStage] || q.pipeline_stage}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <div className="flex items-center gap-3">
                        {q.event_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {format(new Date(q.event_date), "MMM d, yyyy")}
                          </span>
                        )}
                        <span>{SERVICE_TYPE_LABELS[q.service_type as ServiceType] || q.service_type}</span>
                      </div>
                      <span className="flex items-center gap-1 font-medium text-foreground">
                        <DollarSign className="h-3 w-3" />
                        {Number(q.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Timestamps */}
          <div className="text-xs text-muted-foreground space-y-1">
            <p>Created {format(new Date(contact.created_at), "MMM d, yyyy h:mm a")}</p>
            <p>Updated {format(new Date(contact.updated_at), "MMM d, yyyy h:mm a")}</p>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
